/**
 * Phase 8 · Cluster C-48 · Autonomous mode toggle.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.C
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §10.2 rows 6 + 7
 *
 * Segmented two-position control rendered in the AgentComposer footer.
 * ON allows the runner to execute a SINGLE reversible write call
 * without a Plan card gate, provided the tool isn't on the hardcoded
 * safety list (`AGENT_SAFETY_REQUIRE_APPROVAL`). Multi-step plans and
 * any call that lands on the safety list still require manual approval.
 *
 * Persistence: the spec calls for
 * `ggj_b2b_org_users.prefs.agent_autonomous` but that column doesn't
 * exist yet. We persist to `localStorage` under `ggj.agent.autonomous`
 * for now and a follow-up migration / service call can transparently
 * pull the same key and back-fill the DB. The shape (`'on'` | `'off'`)
 * is intentionally narrow so moving storage doesn't require a format
 * change.
 *
 * Safety-rules tooltip: the hover card lists every tool on the
 * hardcoded safety list (from `src/modules/agent/safety.ts`) so the
 * user can see exactly what "autonomous" does and doesn't cover.
 */

import { useCallback, useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import {
  AGENT_SAFETY_REQUIRE_APPROVAL,
  AGENT_SAFETY_RULES_SUMMARY,
} from '../safety';

const STORAGE_KEY = 'ggj.agent.autonomous';

interface Props {
  value: boolean;
  onChange: (next: boolean) => void;
  /** Hide the label above the toggle — useful on narrow composers. */
  compact?: boolean;
}

export function AgentAutonomousToggle({ value, onChange, compact = false }: Props) {
  const [showTip, setShowTip] = useState(false);

  return (
    <div className="relative inline-flex items-center gap-1.5">
      {!compact ? (
        <span
          className="text-[10px] font-medium uppercase tracking-wider"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          Autonomous
        </span>
      ) : null}

      <div
        role="radiogroup"
        aria-label="Autonomous mode"
        className="inline-flex overflow-hidden rounded-[8px] border"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <Option label="Off" selected={!value} onClick={() => onChange(false)} />
        <Option label="On" selected={value} onClick={() => onChange(true)} accent />
      </div>

      <button
        type="button"
        onMouseEnter={() => setShowTip(true)}
        onMouseLeave={() => setShowTip(false)}
        onFocus={() => setShowTip(true)}
        onBlur={() => setShowTip(false)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full"
        style={{ color: 'var(--vscode-text-tertiary)' }}
        aria-label="Show autonomous-mode safety rules"
      >
        <Info className="h-3 w-3" aria-hidden />
      </button>

      {showTip ? (
        <div
          role="tooltip"
          className="absolute bottom-full right-0 z-40 mb-1 w-[260px] rounded-[8px] border p-2.5 text-[11px] shadow-lg"
          style={{
            background: 'var(--vscode-bg-elevated)',
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-primary)',
          }}
        >
          <p className="mb-1.5 font-semibold">Safety rules</p>
          <p className="mb-1.5" style={{ color: 'var(--vscode-text-secondary)' }}>
            {AGENT_SAFETY_RULES_SUMMARY}
          </p>
          <p
            className="mb-1 text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            Always manual approval:
          </p>
          <ul className="flex flex-col gap-0.5">
            {AGENT_SAFETY_REQUIRE_APPROVAL.map((name) => (
              <li
                key={name}
                className="font-mono text-[10px]"
                style={{ color: 'var(--vscode-text-secondary)' }}
              >
                {name}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function Option({
  label,
  selected,
  onClick,
  accent = false,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className="px-2 py-0.5 text-[11px] font-medium transition-colors"
      style={{
        background: selected
          ? accent
            ? 'var(--ggj-accent,#10B981)'
            : 'var(--vscode-bg-hover)'
          : 'transparent',
        color: selected
          ? accent
            ? 'white'
            : 'var(--vscode-text-primary)'
          : 'var(--vscode-text-secondary)',
      }}
    >
      {label}
    </button>
  );
}

/**
 * Convenience hook that centralises read/write of the autonomous flag.
 * Returns the current value and a setter that writes through
 * `localStorage`. The hook is safe on SSR (falls back to `false`).
 *
 * The storage format is a raw `'on' | 'off'` string — intentionally
 * narrower than a JSON blob so a future migration to
 * `ggj_b2b_org_users.prefs.agent_autonomous` can reuse the same key
 * without bumping a format version.
 */
export function useAgentAutonomousMode(): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => readStored());

  useEffect(() => {
    // Keep tabs in sync — toggling on one open surface should update
    // another that's already mounted.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setValue(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const set = useCallback((next: boolean) => {
    setValue(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
    } catch {
      /* quota / private mode — UI still behaves, just doesn't persist */
    }
  }, []);

  return [value, set];
}

function readStored(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}
